import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'

const alertVariants = cva(
	'relative w-full rounded-[var(--radius)] px-4 py-2 mb-4 text-sm',
	{
		variants: {
			variant: {
				primary: 'bg-primary text-primary-foreground',
				success: 'bg-success text-success-foreground',
				danger: 'bg-destructive text-destructive-foreground',
				warning: 'bg-warning text-warning-foreground',
				info: 'bg-info text-info-foreground',
			},
		},
		defaultVariants: {
			variant: 'primary',
		},
	},
)

export interface AlertProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof alertVariants> {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
	({ className, variant, children, ...props }, ref) => (
		<div
			ref={ref}
			role="alert"
			data-testid="ShadcnAlert"
			className={cn(alertVariants({ variant }), className)}
			{...props}
		>
			{children}
		</div>
	),
)
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h5
		ref={ref}
		className={cn('mb-1 font-medium leading-none tracking-tight', className)}
		{...props}
	/>
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn('text-sm [&_p]:leading-relaxed', className)}
		{...props}
	/>
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription, alertVariants }
